/**
 * React Query hooks for Creative Analytics Dashboard (Phase 28).
 *
 * Each hook wraps an API service call with proper query keys,
 * stale time configuration, and team-scoped auth from AuthContext.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useState, useCallback } from 'react';

import { useAuth } from '@/context/AuthContext';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import {
  getVerdictSummary,
  getGateSummary,
  getKpiDrivers,
  getComposition,
  getCalibration,
  getExplorer,
  getCreatives,
  getReports,
  createReport,
  updateReport,
  deleteReport,
  previewReport,
  exportReportCsv,
  getHomeMetrics,
  getReportOptions,
  type HomeMetrics,
} from '@/services/dashboard';
import type {
  DashboardFilters,
  VerdictSummary,
  GateSummary,
  KpiDriverGroup,
  CompositionData,
  CalibrationData,
  ExplorerData,
  CreativesListResponse,
  SavedReport,
  ReportConfig,
  ReportPreviewResponse,
  ReportOptions,
  GroupField,
  ShareBasis,
} from '@/types/dashboard';

// ============================================================================
// CONSTANTS
// ============================================================================

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const GC_TIME = 10 * 60 * 1000; // 10 minutes

// ============================================================================
// QUERY KEYS
// ============================================================================

export const dashboardKeys = {
  all: ['dashboard'] as const,
  homeMetrics: (teamId: string) =>
    [...dashboardKeys.all, 'home-metrics', teamId] as const,
  verdicts: (filters: DashboardFilters) =>
    [...dashboardKeys.all, 'verdicts', filters] as const,
  gates: (filters: DashboardFilters) =>
    [...dashboardKeys.all, 'gates', filters] as const,
  drivers: (filters: DashboardFilters, kpiId: string, scope: string) =>
    [...dashboardKeys.all, 'drivers', filters, kpiId, scope] as const,
  composition: (
    filters: DashboardFilters,
    kpiId: string,
    groupField: GroupField,
    shareBasis: ShareBasis
  ) =>
    [
      ...dashboardKeys.all,
      'composition',
      filters,
      kpiId,
      groupField.kind,
      groupField.id,
      shareBasis,
    ] as const,
  calibration: (filters: DashboardFilters, xAxis?: string) =>
    [...dashboardKeys.all, 'calibration', filters, xAxis] as const,
  explorer: (filters: DashboardFilters, xAxis: string, yAxis: string) =>
    [...dashboardKeys.all, 'explorer', filters, xAxis, yAxis] as const,
  creatives: (
    filters: DashboardFilters,
    page: number,
    pageSize: number,
    sort: string,
    order: string,
    gates?: string[],
    search?: string
  ) =>
    [...dashboardKeys.all, 'creatives', filters, page, pageSize, sort, order, gates, search] as const,
  reports: (teamId: string) =>
    [...dashboardKeys.all, 'reports', teamId] as const,
  // Phase 37 D-03: config.templateId participates in the cache key via
  // tanstack-query's deep-equality hash of the config object — no explicit
  // tuple element needed. Switching template invalidates this key automatically.
  reportPreview: (teamId: string, config: ReportConfig) =>
    [...dashboardKeys.all, 'report-preview', teamId, config] as const,
};

// ============================================================================
// HOME PAGE METRICS
// ============================================================================

export function useHomeMetrics(): UseQueryResult<HomeMetrics, Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  return useQuery({
    queryKey: dashboardKeys.homeMetrics(teamId!),
    queryFn: () => getHomeMetrics(teamId!),
    enabled: !!teamId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ============================================================================
// VERDICTS TAB
// ============================================================================

export function useVerdictSummary(
  filters: DashboardFilters
): UseQueryResult<VerdictSummary, Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  return useQuery({
    queryKey: dashboardKeys.verdicts(filters),
    queryFn: () => getVerdictSummary(teamId!, filters),
    enabled: !!teamId && !!filters.templateId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ============================================================================
// GATES TAB
// ============================================================================

export function useGateSummary(
  filters: DashboardFilters,
  columnGroup: string = 'placement'
): UseQueryResult<GateSummary, Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  return useQuery({
    queryKey: [...dashboardKeys.gates(filters), columnGroup],
    queryFn: () => getGateSummary(teamId!, filters, columnGroup),
    enabled: !!teamId && !!filters.templateId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ============================================================================
// INSIGHTS TAB — KPI DRIVERS
// ============================================================================

export function useKpiDrivers(
  filters: DashboardFilters,
  kpiId: string,
  scope: 'smart' | 'goal' | 'bucket' | 'all'
): UseQueryResult<KpiDriverGroup, Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  return useQuery({
    queryKey: dashboardKeys.drivers(filters, kpiId, scope),
    queryFn: () => getKpiDrivers(teamId!, filters, kpiId, scope),
    enabled: !!teamId && !!filters.templateId && !!kpiId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ============================================================================
// INSIGHTS TAB — COMPOSITION
// ============================================================================

export function useComposition(
  filters: DashboardFilters,
  kpiId: string,
  groupField: GroupField,
  shareBasis: ShareBasis
): UseQueryResult<CompositionData, Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  return useQuery({
    queryKey: dashboardKeys.composition(filters, kpiId, groupField, shareBasis),
    queryFn: () => getComposition(teamId!, filters, kpiId, groupField, shareBasis),
    enabled: !!teamId && !!filters.templateId && !!kpiId && !!groupField.id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ============================================================================
// INSIGHTS TAB — CALIBRATION
// ============================================================================

export function useCalibration(
  filters: DashboardFilters,
  xAxis?: string
): UseQueryResult<CalibrationData, Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  return useQuery({
    queryKey: dashboardKeys.calibration(filters, xAxis),
    queryFn: () => getCalibration(teamId!, filters, xAxis),
    enabled: !!teamId && !!filters.templateId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ============================================================================
// INSIGHTS TAB — EXPLORER
// ============================================================================

export function useExplorer(
  filters: DashboardFilters,
  xAxis: string,
  yAxis: string
): UseQueryResult<ExplorerData, Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  return useQuery({
    queryKey: dashboardKeys.explorer(filters, xAxis, yAxis),
    queryFn: () => getExplorer(teamId!, filters, xAxis, yAxis),
    enabled: !!teamId && !!filters.templateId && !!xAxis && !!yAxis,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ============================================================================
// CREATIVES TAB
// ============================================================================

export function useCreativesList(
  filters: DashboardFilters,
  page: number,
  pageSize: number,
  sort: string,
  order: 'asc' | 'desc',
  gates?: string[],
  search?: string
): UseQueryResult<CreativesListResponse, Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  return useQuery({
    queryKey: dashboardKeys.creatives(filters, page, pageSize, sort, order, gates, search),
    queryFn: () => getCreatives(teamId!, filters, page, pageSize, sort, order, gates, search),
    enabled: !!teamId && !!filters.templateId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ============================================================================
// REPORT OPTIONS (Phase 36-02)
// ============================================================================

export function useReportOptions(): UseQueryResult<ReportOptions, Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;
  const { filters } = useDashboardContext();
  const templateId = filters.templateId;
  return useQuery({
    queryKey: [...dashboardKeys.all, 'report-options', teamId, templateId] as const,
    queryFn: () => getReportOptions(teamId!, templateId),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes — options change rarely
  });
}

// ============================================================================
// REPORTS TAB — QUERIES
// ============================================================================

export function useReports(): UseQueryResult<SavedReport[], Error> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  return useQuery({
    queryKey: dashboardKeys.reports(teamId ?? ''),
    queryFn: () => getReports(teamId!),
    enabled: !!teamId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useReportPreview(
  config: ReportConfig
): UseQueryResult<ReportPreviewResponse, Error> {
  // Phase 37 Plan 05: previewReport now returns the full {data, meta} envelope
  // so the Builder can read meta.capApplied / meta.warning / meta.totalRowCount.
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;

  // Only fetch when config has at least 1 row dimension and 1 metric
  const hasMinimumConfig =
    config.rowDimensions.length >= 1 && config.metrics.length >= 1;

  return useQuery({
    queryKey: dashboardKeys.reportPreview(teamId ?? '', config),
    queryFn: () => previewReport(teamId!, config),
    enabled: !!teamId && hasMinimumConfig,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ============================================================================
// REPORTS TAB — MUTATIONS
// ============================================================================

export function useCreateReport(): UseMutationResult<
  SavedReport,
  Error,
  ReportConfig
> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: ReportConfig) => createReport(teamId!, config),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.reports(teamId ?? ''),
      });
    },
  });
}

export function useUpdateReport(): UseMutationResult<
  SavedReport,
  Error,
  { reportId: string; config: Partial<ReportConfig> }
> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reportId, config }) => updateReport(teamId!, reportId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.reports(teamId ?? ''),
      });
    },
  });
}

export function useDeleteReport(): UseMutationResult<void, Error, string> {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reportId: string) => deleteReport(teamId!, reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.reports(teamId ?? ''),
      });
    },
  });
}

// ============================================================================
// REPORTS TAB — EXPORT
// ============================================================================

export function useExportReport() {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId;
  const [exportingId, setExportingId] = useState<string | null>(null);

  const exportReport = useCallback(
    async (reportId: string, reportName: string) => {
      if (!teamId) return;
      setExportingId(reportId);
      try {
        const blob = await exportReportCsv(teamId, reportId);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportName || 'report'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } finally {
        setExportingId(null);
      }
    },
    [teamId]
  );

  return { exportReport, exportingId };
}
